import { createSlice } from './index';
import { isNil } from 'lodash';
import { getEndDateDST, getStartDateDST } from '../../utils/urlFunctions';
import { downloadFile } from '../../utils';
import { getChickenMaterials } from '../../api/materials';
import { getWasteHistory, getWasteTotal, getWasteAnalytics, getWasteReport } from '../../api/waste';

export const wasteSlice = createSlice({
  name: 'waste',

  initialState: {
    // filters
    restaurantUuid: null,
    timezone: undefined,
    materials: [],
    materialOptions: [],
    interval: null,
    startDate: null,
    endDate: null,
    timeInterval: null,

    // pagination
    limit: 20,
    sortBy: 'eventTime',
    orderDirection: 'DESC',
    cursor: null,
    cursorDirection: 'FIRST',
    existNext: false,

    // data
    data: [],
    totalCount: null,
    totalWaste: null,
    chartHeader: [],
    graphContent: [],
    loading: {
      data: false,
      scroll: false,
      total: false,
      analytics: false,
      report: false,
    },
  },

  reducers: (create) => ({
    resetStore: () => {
      return wasteSlice.getInitialState();
    },

    changeValue: create.preparedReducer(
      (key, value) => {
        return { payload: { key, value } };
      },
      (state, action) => {
        state[action.payload.key] = action.payload.value;
      }
    ),

    changeSeveralValues: (state, action) => {
      for (const key in action.payload) {
        state[key] = action.payload[key];
      }
    },

    fetchMaterialOptions: create.asyncThunk(
      async () => {
        const { results } = await getChickenMaterials();
        return results.content;
      },
      {
        fulfilled: (state, action) => {
          state.materialOptions = action.payload.map((material) => ({
            label: material.name,
            value: material.name,
            color: material.color,
          }));
          state.materials = action.payload.map((material) => material.name);
        },
      }
    ),

    fetchWasteHistory: create.asyncThunk(
      async (state) => {
        const { results } = await getWasteHistory({
          // filters
          restaurantUuid: state.restaurantUuid === 'select' ? null : state.restaurantUuid,
          materials: state.materials.join(','),
          startTime: getStartDateDST(state.startDate, state.timeInterval, state.timezone),
          endTime: getEndDateDST(state.endDate, state.timeInterval, state.timezone),
          // pagination
          limit: state.limit,
          sortBy: state.sortBy,
          orderDirection: state.orderDirection,
          cursorDirection: 'FIRST',
        });

        return results;
      },
      {
        pending: (state) => {
          state.loading.data = true;
        },
        fulfilled: (state, action) => {
          state.data = action.payload.content;
          state.cursor = action.payload.cursor;
          state.cursorDirection = action.payload.existsNextPage ? 'NEXT' : 'LAST';
          state.existNext = action.payload.existsNextPage;
        },
        settled: (state) => {
          state.loading.data = false;
        },
      }
    ),

    fetchWasteHistoryNext: create.asyncThunk(
      async (_, { getState }) => {
        const state = getState().waste;

        const { results } = await getWasteHistory({
          // filters
          restaurantUuid: state.restaurantUuid === 'select' ? null : state.restaurantUuid,
          materials: state.materials.join(','),
          startTime: getStartDateDST(state.startDate, state.timeInterval, state.timezone),
          endTime: getEndDateDST(state.endDate, state.timeInterval, state.timezone),
          // pagination
          limit: state.limit,
          sortBy: state.sortBy,
          orderDirection: state.orderDirection,
          cursor: state.cursor,
          cursorDirection: state.cursorDirection,
        });

        return results;
      },
      {
        pending: (state) => {
          state.loading.scroll = true;
        },
        fulfilled: (state, action) => {
          state.data = [...state.data, ...action.payload.content];
          state.cursor = action.payload.cursor;
          state.cursorDirection = action.payload.existsNextPage ? 'NEXT' : 'LAST';
          state.existNext = action.payload.existsNextPage;
        },
        settled: (state) => {
          state.loading.scroll = false;
        },
      }
    ),

    fetchWasteTotal: create.asyncThunk(
      async (state) => {
        const { results } = await getWasteTotal({
          restaurantUuid: state.restaurantUuid === 'select' ? null : state.restaurantUuid,
          materials: state.materials.join(','),
          startTime: getStartDateDST(state.startDate, state.timeInterval, state.timezone),
          endTime: getEndDateDST(state.endDate, state.timeInterval, state.timezone),
        });

        return results;
      },
      {
        pending: (state) => {
          state.loading.total = true;
        },
        fulfilled: (state, action) => {
          state.totalCount = action.payload.totalCount;
          state.totalWaste = action.payload.totalWastedAmount && action.payload.totalWastedAmount / 1000;
        },
        settled: (state) => {
          state.loading.total = false;
        },
      }
    ),

    fetchWasteAnalytics: create.asyncThunk(
      async (state) => {
        const { results } = await getWasteAnalytics({
          restaurantsUuid: state.restaurantUuid === 'select' ? null : state.restaurantUuid,
          materials: state.materials.join(','),
          interval: state.interval,
          startEventTime: getStartDateDST(state.startDate, state.timeInterval, state.timezone),
          endEventTime: getEndDateDST(state.endDate, state.timeInterval, state.timezone),
        });

        return results;
      },
      {
        pending: (state) => {
          state.loading.analytics = true;
        },
        fulfilled: (state, action) => {
          const results = action.payload;
          state.chartHeader = results.length !== 0 ? Object.keys(results[0].values[0][1]) : [];
          state.graphContent =
            results.length !== 0
              ? results[0].values.map((item, index) => {
                  return [
                    new Date(item[0] * 1000),
                    ...Object.keys(item[1]).map((material) => {
                      return results.reduce(function (sum, elem) {
                        return (
                          sum +
                          Number(elem.values[index][1][material] > 0 ? elem.values[index][1][material] : 0)
                        );
                      }, 0);
                    }),
                  ];
                })
              : [[0, 0]];
        },
        settled: (state) => {
          state.loading.analytics = false;
        },
      }
    ),

    downloadWasteReport: create.asyncThunk(
      async (params, { getState, rejectWithValue }) => {
        const state = getState().waste;

        try {
          const response = await getWasteReport({
            restaurantUuid: state.restaurantUuid === 'select' ? null : state.restaurantUuid,
            materials: state.materials.join(','),
            startTime: getStartDateDST(state.startDate, state.timeInterval, state.timezone),
            endTime: getEndDateDST(state.endDate, state.timeInterval, state.timezone),
            ...params,
          });

          if (response.code !== 202) downloadFile(response.results, 'application/zip');

          return response;
        } catch (error) {
          return rejectWithValue(error.message);
        }
      },
      {
        pending: (state) => {
          state.loading.report = true;
        },
        settled: (state) => {
          state.loading.report = false;
        },
      }
    ),
  }),
});
